import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDoc, getDocs, updateDoc, deleteDoc, doc, query, where, arrayUnion, Timestamp } from "firebase/firestore";

const SYSTEM_ADMIN = "carewave0614@gmail.com";

// ===== 会社お知らせタブ =====
// 既読状態は users/{uid} の readNoticeIds（お知らせIDの配列）に持たせる。
// 既存のFirestoreルール（本人は自分のusersドキュメントを更新可）だけで動く。
function CompanyNoticeTab({ user, isAdmin, companyId, pendingCount, onOpenRequests, onUnreadChange }) {
  const [notices, setNotices] = useState([]);
  const [readIds, setReadIds] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { fetchNotices(); }, []);

  const fetchNotices = async () => {
    try {
      const q = companyId
        ? query(collection(db, "notices"), where("companyId", "==", companyId))
        : collection(db, "notices");
      const [snap, userSnap] = await Promise.all([
        getDocs(q),
        getDoc(doc(db, "users", user.uid)),
      ]);
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt);
      const ids = userSnap.exists() ? (userSnap.data().readNoticeIds || []) : [];
      setNotices(rows);
      setReadIds(ids);
      if (onUnreadChange) onUnreadChange(rows.filter(n => !ids.includes(n.id)).length);
    } catch (e) { console.error("お知らせの取得に失敗しました:", e); }
  };

  // 既読にする（画面は即座に反映し、保存は裏で行う）
  const markRead = async (ids) => {
    const fresh = ids.filter(id => !readIds.includes(id));
    if (fresh.length === 0) return;
    const next = [...readIds, ...fresh];
    setReadIds(next);
    if (onUnreadChange) onUnreadChange(notices.filter(n => !next.includes(n.id)).length);
    try {
      await updateDoc(doc(db, "users", user.uid), { readNoticeIds: arrayUnion(...fresh) });
    } catch (e) {
      console.error("既読の保存に失敗しました:", e);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    setPosting(true); setError("");
    try {
      await addDoc(collection(db, "notices"), {
        title, body, companyId: companyId || "",
        authorEmail: user.email, createdAt: Timestamp.now(),
      });
      setTitle(""); setBody(""); setShowForm(false);
      fetchNotices();
    } catch { setError("投稿に失敗しました。もう一度お試しください。"); }
    finally { setPosting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("このお知らせを削除しますか？")) return;
    try { await deleteDoc(doc(db, "notices", id)); fetchNotices(); }
    catch { alert("削除に失敗しました。"); }
  };

  const fmt = ts => ts?.toDate().toLocaleDateString("ja-JP");
  const isUnread = n => !readIds.includes(n.id);
  const unreadCount = notices.filter(isUnread).length;

  return (
    <div>
      {pendingCount > 0 && (
        <div style={st.alertBanner} onClick={onOpenRequests}>
          <span style={{ fontSize: "22px" }}>🔔</span>
          <span style={{ flex: 1 }}>従業員から<strong>{pendingCount}件</strong>の申請・書類が届いています。</span>
          <span style={st.alertLink}>確認する →</span>
        </div>
      )}
      <div style={st.toolbar}>
        <div style={st.unreadSummary}>
          {unreadCount > 0
            ? <><span style={st.unreadDot} />未読 {unreadCount}件</>
            : <span style={{ color: "#95a5a6" }}>未読はありません</span>}
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {unreadCount > 0 && (
            <button style={st.allReadBtn} onClick={() => markRead(notices.map(n => n.id))}>すべて既読にする</button>
          )}
          {isAdmin && (
            <button style={st.addBtn} onClick={() => setShowForm(!showForm)}>{showForm ? "キャンセル" : "＋ 投稿"}</button>
          )}
        </div>
      </div>
      {showForm && (
        <form onSubmit={handlePost} style={st.form}>
          <input style={st.input} type="text" placeholder="タイトル" value={title} onChange={e => setTitle(e.target.value)} required />
          <textarea style={{ ...st.input, height: "120px", resize: "vertical" }} placeholder="本文" value={body} onChange={e => setBody(e.target.value)} required />
          {error && <p style={{ color: "#e74c3c", fontSize: "14px", marginBottom: "8px" }}>{error}</p>}
          <button style={st.postBtn} type="submit" disabled={posting}>{posting ? "投稿中..." : "投稿する"}</button>
        </form>
      )}
      {notices.length === 0
        ? <p style={st.empty}>お知らせはありません</p>
        : notices.map(n => (
          <div key={n.id} style={{ ...st.card, ...(isUnread(n) ? st.unreadCard : {}) }}>
            <div style={st.cardHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                {isUnread(n) && <span style={st.unreadBadge}>未読</span>}
                <h3 style={{ ...st.noticeTitle, ...(isUnread(n) ? st.unreadTitle : st.readTitle) }}>{n.title}</h3>
              </div>
              {isAdmin && <button style={st.deleteBtn} onClick={() => handleDelete(n.id)}>削除</button>}
            </div>
            <p style={st.noticeBody}>{n.body}</p>
            <div style={st.noticeFooter}>
              <span style={st.meta}>{fmt(n.createdAt)} · {n.authorEmail}</span>
              {isUnread(n)
                ? <button style={st.readBtn} onClick={() => markRead([n.id])}>既読にする</button>
                : <span style={st.readMark}>✓ 既読</span>}
            </div>
          </div>
        ))
      }
    </div>
  );
}

// ===== システム情報タブ =====
function SystemInfoTab({ user }) {
  const isOperator = user.email === SYSTEM_ADMIN;
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tag, setTag] = useState("メンテナンス");
  const [posting, setPosting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");

  const TAGS = ["メンテナンス", "アップデート", "障害情報", "お知らせ"];
  const TAG_COLOR = {
    "メンテナンス": "#e67e22",
    "アップデート": "#27ae60",
    "障害情報":     "#e74c3c",
    "お知らせ":     "#2980b9",
  };

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const snap = await getDocs(collection(db, "systemInfo"));
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt));
    } catch (e) { console.error(e); }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    setPosting(true); setError("");
    try {
      await addDoc(collection(db, "systemInfo"), {
        title, body, tag, authorEmail: user.email, createdAt: Timestamp.now(),
      });
      setTitle(""); setBody(""); setShowForm(false);
      fetchItems();
    } catch { setError("投稿に失敗しました。"); }
    finally { setPosting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("削除しますか？")) return;
    try { await deleteDoc(doc(db, "systemInfo", id)); fetchItems(); }
    catch { alert("削除に失敗しました。"); }
  };

  const fmt = ts => ts?.toDate().toLocaleDateString("ja-JP");

  return (
    <div>
      {isOperator && (
        <div style={{ marginBottom: "16px", textAlign: "right" }}>
          <button style={st.addBtn} onClick={() => setShowForm(!showForm)}>{showForm ? "キャンセル" : "＋ 投稿"}</button>
        </div>
      )}
      {showForm && (
        <form onSubmit={handlePost} style={st.form}>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ fontSize: "14px", color: "#2c3e50", fontWeight: "bold", display: "block", marginBottom: "6px" }}>カテゴリ</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {TAGS.map(t => (
                <button key={t} type="button"
                  style={{ padding: "6px 16px", borderRadius: "20px", border: "none", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
                    backgroundColor: tag === t ? TAG_COLOR[t] : "#f0f4f8",
                    color: tag === t ? "#fff" : "#2c3e50" }}
                  onClick={() => setTag(t)}>{t}</button>
              ))}
            </div>
          </div>
          <input style={st.input} type="text" placeholder="タイトル" value={title} onChange={e => setTitle(e.target.value)} required />
          <textarea style={{ ...st.input, height: "120px", resize: "vertical" }} placeholder="内容" value={body} onChange={e => setBody(e.target.value)} required />
          {error && <p style={{ color: "#e74c3c", fontSize: "14px", marginBottom: "8px" }}>{error}</p>}
          <button style={st.postBtn} type="submit" disabled={posting}>{posting ? "投稿中..." : "投稿する"}</button>
        </form>
      )}
      {items.length === 0
        ? <p style={st.empty}>現在、システム情報はありません</p>
        : items.map(n => (
          <div key={n.id} style={{ ...st.card, borderLeft: `4px solid ${TAG_COLOR[n.tag] || "#2980b9"}` }}>
            <div style={st.cardHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <span style={{ padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold",
                  backgroundColor: TAG_COLOR[n.tag] || "#2980b9", color: "#fff" }}>{n.tag}</span>
                <h3 style={st.noticeTitle}>{n.title}</h3>
              </div>
              {isOperator && <button style={st.deleteBtn} onClick={() => handleDelete(n.id)}>削除</button>}
            </div>
            <p style={st.noticeBody}>{n.body}</p>
            <div style={st.meta}>{fmt(n.createdAt)}</div>
          </div>
        ))
      }
    </div>
  );
}

// ===== 申請通知タブ（書類申請の承認権限を持つ人だけ表示） =====
function DocRequestTab({ docs, onRefresh, onNavigate }) {
  const [working, setWorking] = useState(null);

  const handleStatus = async (id, status) => {
    setWorking(id);
    try {
      await updateDoc(doc(db, "documents", id), { status });
      await onRefresh();
    } catch (e) {
      console.error("申請の更新に失敗しました:", e);
      alert("更新に失敗しました。");
    } finally {
      setWorking(null);
    }
  };

  const fmt = ts => ts?.toDate().toLocaleString("ja-JP", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  const isSend = t => t === "書類送付";

  if (docs.length === 0) {
    return (
      <div>
        <p style={st.empty}>未処理の申請・書類はありません</p>
        {onNavigate && (
          <div style={{ textAlign: "center", marginTop: "16px" }}>
            <button style={st.linkBtn} onClick={() => onNavigate("document")}>書類申請の一覧を見る →</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <p style={{ color: "#7f8c8d", fontSize: "14px", marginTop: 0, marginBottom: "16px" }}>
        従業員から届いた未処理の申請・書類です。この画面から処理できます。
      </p>
      {docs.map(d => (
        <div key={d.id} style={{ ...st.card, borderLeft: "4px solid #f39c12" }}>
          <div style={st.cardHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <span style={st.pendingBadge}>{isSend(d.type) ? "未確認" : "審査中"}</span>
              <h3 style={st.noticeTitle}>{d.type}</h3>
            </div>
            <span style={{ fontSize: "13px", color: "#7f8c8d" }}>{d.email}</span>
          </div>
          {d.title  && <div style={st.reqRow}><span style={st.reqLabel}>件名</span>{d.title}</div>}
          {d.date   && <div style={st.reqRow}><span style={st.reqLabel}>日時</span>{d.date}</div>}
          {d.reason && <div style={st.reqRow}><span style={st.reqLabel}>{isSend(d.type) ? "メモ" : "理由"}</span>{d.reason}</div>}
          {d.attachment && <div style={st.reqRow}><span style={st.reqLabel}>添付</span>{d.attachment.name}（書類申請の画面からダウンロードできます）</div>}
          <div style={st.meta}>{isSend(d.type) ? "送付日時" : "申請日時"}：{fmt(d.createdAt)}</div>
          <div style={{ display: "flex", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
            <button style={st.approveBtn} disabled={working === d.id} onClick={() => handleStatus(d.id, "approved")}>{isSend(d.type) ? "受領" : "承認"}</button>
            <button style={st.rejectBtn}  disabled={working === d.id} onClick={() => handleStatus(d.id, "rejected")}>{isSend(d.type) ? "差し戻し" : "却下"}</button>
            {onNavigate && <button style={st.linkBtn} onClick={() => onNavigate("document")}>詳細を見る →</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ===== メインコンポーネント =====
function Notice({ user, isAdmin, companyId, canApproveDocument = false, onNavigate }) {
  const [tab, setTab] = useState("company");
  const [pendingDocs, setPendingDocs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const mounted = useRef(true);

  // 従業員からの未処理申請（status === "pending"）を通知として拾う
  const fetchPending = useCallback(async () => {
    if (!canApproveDocument) { setPendingDocs([]); return; }
    try {
      const q = companyId
        ? query(collection(db, "documents"), where("companyId", "==", companyId), where("status", "==", "pending"))
        : query(collection(db, "documents"), where("status", "==", "pending"));
      const snap = await getDocs(q);
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt);
      if (mounted.current) setPendingDocs(rows);
    } catch (e) {
      console.error("申請通知の取得に失敗しました:", e);
    }
  }, [canApproveDocument, companyId]);

  useEffect(() => {
    mounted.current = true;
    fetchPending();
    return () => { mounted.current = false; };
  }, [fetchPending]);

  const tabs = [
    { key: "company", label: "📢 会社のお知らせ", badge: unreadCount },
    ...(canApproveDocument ? [{ key: "requests", label: "🔔 申請通知", badge: pendingDocs.length }] : []),
    { key: "system",  label: "🔧 システム情報" },
  ];

  return (
    <div style={st.container}>
      <h2 style={st.title}>お知らせ</h2>
      <div style={st.tabs}>
        {tabs.map(t => (
          <button key={t.key} style={{ ...st.tab, ...(tab === t.key ? st.activeTab : {}) }} onClick={() => setTab(t.key)}>
            {t.label}
            {t.badge > 0 && <span style={st.tabBadge}>{t.badge}</span>}
          </button>
        ))}
      </div>
      {tab === "company"  && <CompanyNoticeTab user={user} isAdmin={isAdmin} companyId={companyId}
                               pendingCount={pendingDocs.length} onOpenRequests={() => setTab("requests")}
                               onUnreadChange={setUnreadCount} />}
      {tab === "requests" && <DocRequestTab docs={pendingDocs} onRefresh={fetchPending} onNavigate={onNavigate} />}
      {tab === "system"   && <SystemInfoTab user={user} />}
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "800px", margin: "0 auto" },
  title: { color: "#2c3e50", margin: "0 0 20px" },
  tabs: { display: "flex", gap: "8px", marginBottom: "24px", borderBottom: "2px solid #eee" },
  tab: { padding: "10px 20px", border: "none", backgroundColor: "transparent", cursor: "pointer", fontSize: "15px", color: "#7f8c8d", borderBottom: "3px solid transparent", marginBottom: "-2px" },
  activeTab: { color: "#2c3e50", fontWeight: "bold", borderBottom: "3px solid #2c3e50" },
  addBtn: { padding: "10px 20px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" },
  form: { backgroundColor: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "24px" },
  input: { width: "100%", padding: "12px", marginBottom: "12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", boxSizing: "border-box" },
  postBtn: { padding: "12px 32px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  empty: { color: "#999", textAlign: "center", marginTop: "40px" },
  card: { backgroundColor: "#fff", padding: "20px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" },
  noticeTitle: { color: "#2c3e50", margin: 0, fontSize: "17px" },
  noticeBody: { color: "#555", lineHeight: "1.7", marginBottom: "8px", whiteSpace: "pre-wrap" },
  meta: { fontSize: "12px", color: "#999" },
  deleteBtn: { padding: "4px 12px", backgroundColor: "#e74c3c", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", flexShrink: 0 },
  // 申請通知
  tabBadge: { display: "inline-block", marginLeft: "6px", minWidth: "18px", padding: "1px 6px", borderRadius: "10px", backgroundColor: "#e74c3c", color: "#fff", fontSize: "12px", fontWeight: "bold", lineHeight: "16px", textAlign: "center" },
  alertBanner: { display: "flex", alignItems: "center", gap: "12px", backgroundColor: "#fff6e5", border: "1px solid #f5c26b", borderRadius: "10px", padding: "14px 18px", marginBottom: "20px", cursor: "pointer", color: "#8a5a00", fontSize: "14px" },
  alertLink: { color: "#e67e22", fontWeight: "bold", whiteSpace: "nowrap" },
  pendingBadge: { padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold", backgroundColor: "#f39c1222", color: "#f39c12", whiteSpace: "nowrap" },
  reqRow: { fontSize: "14px", color: "#555", marginBottom: "6px", display: "flex", gap: "8px", alignItems: "flex-start" },
  reqLabel: { color: "#999", fontSize: "13px", minWidth: "36px", flexShrink: 0, paddingTop: "2px" },
  approveBtn: { padding: "8px 20px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" },
  rejectBtn:  { padding: "8px 20px", backgroundColor: "#e74c3c", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" },
  linkBtn:    { padding: "8px 16px", backgroundColor: "transparent", color: "#2980b9", border: "none", cursor: "pointer", fontSize: "14px" },
  // 未読・既読
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "16px", flexWrap: "wrap" },
  unreadSummary: { display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#e74c3c", fontWeight: "bold" },
  unreadDot: { display: "inline-block", width: "9px", height: "9px", borderRadius: "50%", backgroundColor: "#e74c3c" },
  allReadBtn: { padding: "10px 16px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #ccd", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  unreadCard: { borderLeft: "4px solid #e74c3c", backgroundColor: "#fffdfd" },
  unreadBadge: { padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold", backgroundColor: "#e74c3c", color: "#fff", whiteSpace: "nowrap" },
  unreadTitle: { fontWeight: "bold" },
  readTitle: { fontWeight: "normal", color: "#5d6d7e" },
  noticeFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" },
  readBtn: { padding: "5px 14px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  readMark: { fontSize: "12px", color: "#95a5a6" },
};

export default Notice;
